import type { NbaTiebreakerTeam, TiebreakerResult, GameResultsLookup, TiebreakerOptions, StepContext } from '../../shared/tiebreaker/types';
import { RankMethod } from '../../shared/enums/rank-method';
import { createContext } from '../../shared/tiebreaker/step-runner';
import {
  groupByDescending, bestOf, computeHthRecords, hthWinPct,
  groupByHthPct, filterDivisionWinners,
  addExplanation, addExplanationAll,
} from '../../shared/tiebreaker/helpers';

export interface NbaTiebreakerOptions extends TiebreakerOptions {}

/**
 * NBA Two-way tiebreaker.
 * HTH → divisionWinner → same-division check → div% (if same div) → conf% → coinflip
 */
export function nbaTwoWayTiebreaker(
  teams: NbaTiebreakerTeam[],
  games: GameResultsLookup,
  options?: NbaTiebreakerOptions,
): TiebreakerResult<NbaTiebreakerTeam> {
  const ctx = createContext<NbaTiebreakerTeam>(games, options);
  const [a, b] = teams;

  // Step 1: H2H
  const hthRecords = computeHthRecords(teams, ctx.games);
  const aPct = hthWinPct(hthRecords, a.teamAbbreviation);
  const bPct = hthWinPct(hthRecords, b.teamAbbreviation);

  if (aPct > bPct && aPct > 0) {
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.HTHTwoWay);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.HTHTwoWay);
    return mkResult(ctx, [a, b]);
  }
  if (bPct > aPct && bPct > 0) {
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.HTHTwoWay);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.HTHTwoWay);
    return mkResult(ctx, [b, a]);
  }

  // Step 2: Division winner
  if (a.isDivisionWinner && !b.isDivisionWinner) {
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.DivisionWinner);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.NonDivisionWinner);
    return mkResult(ctx, [a, b]);
  }
  if (b.isDivisionWinner && !a.isDivisionWinner) {
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.DivisionWinner);
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.NonDivisionWinner);
    return mkResult(ctx, [b, a]);
  }

  // Step 3: Same-division gating + div%
  if (a.division === b.division) {
    if (a.records.division.pctNaN > b.records.division.pctNaN && a.records.division.pctNaN >= 0) {
      ctx.rankMethods.set(a.teamAbbreviation, RankMethod.TwoWayDivPCT);
      ctx.rankMethods.set(b.teamAbbreviation, RankMethod.TwoWayDivPCT);
      return mkResult(ctx, [a, b]);
    }
    if (b.records.division.pctNaN > a.records.division.pctNaN && b.records.division.pctNaN >= 0) {
      ctx.rankMethods.set(a.teamAbbreviation, RankMethod.TwoWayDivPCT);
      ctx.rankMethods.set(b.teamAbbreviation, RankMethod.TwoWayDivPCT);
      return mkResult(ctx, [b, a]);
    }
  }
  // If different divisions, skip div%

  // Step 4: Conf%
  if (a.records.conference.pctNaN > b.records.conference.pctNaN && a.records.conference.pctNaN >= 0) {
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.TwoWayConfPCT);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.TwoWayConfPCT);
    return mkResult(ctx, [a, b]);
  }
  if (b.records.conference.pctNaN > a.records.conference.pctNaN && b.records.conference.pctNaN >= 0) {
    ctx.rankMethods.set(a.teamAbbreviation, RankMethod.TwoWayConfPCT);
    ctx.rankMethods.set(b.teamAbbreviation, RankMethod.TwoWayConfPCT);
    return mkResult(ctx, [b, a]);
  }

  // Step 5: W% vs playoff teams (own conference)
  const aPctOwn = a.playoffWinPctOwnConf ?? -1;
  const bPctOwn = b.playoffWinPctOwnConf ?? -1;
  if (aPctOwn >= 0 || bPctOwn >= 0) {
    if (aPctOwn > bPctOwn) {
      ctx.rankMethods.set(a.teamAbbreviation, RankMethod.NbaPlayoffWinPctOwnConf);
      ctx.rankMethods.set(b.teamAbbreviation, RankMethod.NbaPlayoffWinPctOwnConf);
      addExplanationAll(ctx, teams, `Playoff W% (own conf): ${a.teamAbbreviation}=${aPctOwn.toFixed(3)}, ${b.teamAbbreviation}=${bPctOwn.toFixed(3)}.`);
      return mkResult(ctx, [a, b]);
    }
    if (bPctOwn > aPctOwn) {
      ctx.rankMethods.set(a.teamAbbreviation, RankMethod.NbaPlayoffWinPctOwnConf);
      ctx.rankMethods.set(b.teamAbbreviation, RankMethod.NbaPlayoffWinPctOwnConf);
      addExplanationAll(ctx, teams, `Playoff W% (own conf): ${b.teamAbbreviation}=${bPctOwn.toFixed(3)}, ${a.teamAbbreviation}=${aPctOwn.toFixed(3)}.`);
      return mkResult(ctx, [b, a]);
    }
  }

  // Step 6: W% vs playoff teams (other conference)
  const aPctOther = a.playoffWinPctOtherConf ?? -1;
  const bPctOther = b.playoffWinPctOtherConf ?? -1;
  if (aPctOther >= 0 || bPctOther >= 0) {
    if (aPctOther > bPctOther) {
      ctx.rankMethods.set(a.teamAbbreviation, RankMethod.NbaPlayoffWinPctOtherConf);
      ctx.rankMethods.set(b.teamAbbreviation, RankMethod.NbaPlayoffWinPctOtherConf);
      addExplanationAll(ctx, teams, `Playoff W% (other conf): ${a.teamAbbreviation}=${aPctOther.toFixed(3)}, ${b.teamAbbreviation}=${bPctOther.toFixed(3)}.`);
      return mkResult(ctx, [a, b]);
    }
    if (bPctOther > aPctOther) {
      ctx.rankMethods.set(a.teamAbbreviation, RankMethod.NbaPlayoffWinPctOtherConf);
      ctx.rankMethods.set(b.teamAbbreviation, RankMethod.NbaPlayoffWinPctOtherConf);
      addExplanationAll(ctx, teams, `Playoff W% (other conf): ${b.teamAbbreviation}=${bPctOther.toFixed(3)}, ${a.teamAbbreviation}=${aPctOther.toFixed(3)}.`);
      return mkResult(ctx, [b, a]);
    }
  }

  // Step 7: Point differential (all games)
  // This application does not record per-game scores; pointDifferential is undefined in practice.
  // When score data is available (future), the pre-computed value on each team will break ties here.
  const aDiff = a.pointDifferential;
  const bDiff = b.pointDifferential;
  if (aDiff !== undefined && bDiff !== undefined) {
    if (aDiff > bDiff) {
      ctx.rankMethods.set(a.teamAbbreviation, RankMethod.NbaPointDifferential);
      ctx.rankMethods.set(b.teamAbbreviation, RankMethod.NbaPointDifferential);
      addExplanationAll(ctx, teams, `Point differential: ${a.teamAbbreviation}=${aDiff.toFixed(1)}, ${b.teamAbbreviation}=${bDiff.toFixed(1)}.`);
      return mkResult(ctx, [a, b]);
    }
    if (bDiff > aDiff) {
      ctx.rankMethods.set(a.teamAbbreviation, RankMethod.NbaPointDifferential);
      ctx.rankMethods.set(b.teamAbbreviation, RankMethod.NbaPointDifferential);
      addExplanationAll(ctx, teams, `Point differential: ${b.teamAbbreviation}=${bDiff.toFixed(1)}, ${a.teamAbbreviation}=${aDiff.toFixed(1)}.`);
      return mkResult(ctx, [b, a]);
    }
  }

  // Step 8 (fallback): Coin flip
  const winner = ctx.coinFlipFn(teams);
  const loser = teams.find(t => t !== winner)!;
  ctx.rankMethods.set(winner.teamAbbreviation, RankMethod.CoinFlip);
  ctx.rankMethods.set(loser.teamAbbreviation, RankMethod.CoinFlip);
  addExplanation(ctx, winner, 'Coin flip.');
  return mkResult(ctx, [winner, loser]);
}

/**
 * NBA Multi-way tiebreaker: resolves 3+ teams.
 * Division-winner separation → HTH → div% (same div only) → conf% → coinflip
 * NO sweep check (unlike MLB).
 * Partial breaks restart from HTH (bug fix).
 */
export function nbaMultiWayTiebreaker(
  teams: NbaTiebreakerTeam[],
  games: GameResultsLookup,
  skipDivWinner: boolean,
  options?: NbaTiebreakerOptions,
): TiebreakerResult<NbaTiebreakerTeam> {
  const ctx = createContext<NbaTiebreakerTeam>(games, options);
  const ordered: NbaTiebreakerTeam[] = [];
  let remaining = [...teams];
  const maxIterations = teams.length * 10;
  let iterations = 0;

  while (remaining.length > 1) {
    if (iterations++ > maxIterations) {
      while (remaining.length > 0) {
        const w = ctx.coinFlipFn(remaining);
        ctx.rankMethods.set(w.teamAbbreviation, RankMethod.CoinFlip);
        ordered.push(w);
        remaining = remaining.filter(t => t !== w);
      }
      break;
    }

    if (remaining.length === 2) {
      const sub = nbaTwoWayTiebreaker(remaining, games, options);
      ordered.push(...sub.orderedTeams);
      mergeResults(ctx, sub);
      remaining = [];
      break;
    }

    // Step 1: Division winner separation
    if (!skipDivWinner) {
      const { divisionWinners, nonWinners } = filterDivisionWinners(remaining);
      if (divisionWinners.length > 0 && nonWinners.length > 0) {
        for (const w of divisionWinners) {
          ctx.rankMethods.set(w.teamAbbreviation, RankMethod.DivisionWinnerMultiWay);
          ordered.push(w);
        }
        remaining = nonWinners;
        continue;
      }
    }

    // No sweep check for NBA (unlike MLB)

    // Step 2: HTH grouping
    const hthGroups = groupByHthPct(remaining, ctx.games);
    if (hthGroups.length > 1) {
      const topGroup = hthGroups[0].teams as NbaTiebreakerTeam[];
      const rest = hthGroups.slice(1).flatMap(g => g.teams) as NbaTiebreakerTeam[];

      if (topGroup.length === 1) {
        ctx.rankMethods.set(topGroup[0].teamAbbreviation, RankMethod.HTHPartialBreakMultiWay);
        ordered.push(topGroup[0]);
        remaining = rest;
        continue;
      }
      if (topGroup.length === 2) {
        const sub = nbaTwoWayTiebreaker(topGroup, games, options);
        ordered.push(...sub.orderedTeams);
        mergeResults(ctx, sub);
      } else {
        const sub = nbaMultiWayTiebreaker(topGroup, games, true, options);
        ordered.push(...sub.orderedTeams);
        mergeResults(ctx, sub);
      }
      remaining = rest;
      continue;
    }

    // Step 3: Div% (only if all remaining teams are in the same division)
    const allSameDiv = remaining.every(t => t.division === remaining[0].division);
    if (allSameDiv) {
      const bestDiv = bestOf(remaining, t => t.records.division.pctNaN);
      if (bestDiv.length < remaining.length && bestDiv.length > 0 && bestDiv[0].records.division.pctNaN >= 0) {
        if (bestDiv.length === 1) {
          ctx.rankMethods.set(bestDiv[0].teamAbbreviation, RankMethod.DivPctFullBreakMultiWay);
          ordered.push(bestDiv[0]);
          remaining = remaining.filter(t => t !== bestDiv[0]);
          continue;
        }
        for (const t of bestDiv) {
          ctx.rankMethods.set(t.teamAbbreviation, RankMethod.DivPctPartialBreakMultiWay);
        }
        if (bestDiv.length === 2) {
          const sub = nbaTwoWayTiebreaker(bestDiv, games, options);
          ordered.push(...sub.orderedTeams);
          mergeResults(ctx, sub);
        } else {
          const sub = nbaMultiWayTiebreaker(bestDiv, games, true, options);
          ordered.push(...sub.orderedTeams);
          mergeResults(ctx, sub);
        }
        remaining = remaining.filter(t => !bestDiv.includes(t));
        continue;
      }
    }

    // Step 4: Conf%
    const bestConf = bestOf(remaining, t => t.records.conference.pctNaN);
    if (bestConf.length < remaining.length && bestConf.length > 0 && bestConf[0].records.conference.pctNaN >= 0) {
      if (bestConf.length === 1) {
        ctx.rankMethods.set(bestConf[0].teamAbbreviation, RankMethod.ConfPctFullBreakMultiWay);
        ordered.push(bestConf[0]);
        remaining = remaining.filter(t => t !== bestConf[0]);
        continue;
      }
      if (bestConf.length === 2) {
        const sub = nbaTwoWayTiebreaker(bestConf, games, options);
        ordered.push(...sub.orderedTeams);
        mergeResults(ctx, sub);
      } else {
        const sub = nbaMultiWayTiebreaker(bestConf, games, true, options);
        ordered.push(...sub.orderedTeams);
        mergeResults(ctx, sub);
      }
      remaining = remaining.filter(t => !bestConf.includes(t));
      continue;
    }

    // Step 5: W% vs playoff teams (own conference)
    const bestPlayoffOwn = bestOf(remaining, t => t.playoffWinPctOwnConf ?? -1);
    const topPlayoffOwnPct = bestPlayoffOwn[0]?.playoffWinPctOwnConf ?? -1;
    if (topPlayoffOwnPct >= 0 && bestPlayoffOwn.length < remaining.length) {
      if (bestPlayoffOwn.length === 1) {
        ctx.rankMethods.set(bestPlayoffOwn[0].teamAbbreviation, RankMethod.NbaPlayoffWinPctOwnConf);
        ordered.push(bestPlayoffOwn[0]);
        remaining = remaining.filter(t => t !== bestPlayoffOwn[0]);
        continue;
      }
      if (bestPlayoffOwn.length === 2) {
        const sub = nbaTwoWayTiebreaker(bestPlayoffOwn, games, options);
        ordered.push(...sub.orderedTeams);
        mergeResults(ctx, sub);
      } else {
        const sub = nbaMultiWayTiebreaker(bestPlayoffOwn, games, true, options);
        ordered.push(...sub.orderedTeams);
        mergeResults(ctx, sub);
      }
      remaining = remaining.filter(t => !bestPlayoffOwn.includes(t));
      continue;
    }

    // Step 6: Point differential (all games)
    // This application does not record per-game scores; pointDifferential is undefined in practice.
    const hasPointDiff = remaining.every(t => t.pointDifferential !== undefined);
    if (hasPointDiff) {
      const bestDiff = bestOf(remaining, t => t.pointDifferential!);
      if (bestDiff.length < remaining.length) {
        if (bestDiff.length === 1) {
          ctx.rankMethods.set(bestDiff[0].teamAbbreviation, RankMethod.NbaPointDifferential);
          ordered.push(bestDiff[0]);
          remaining = remaining.filter(t => t !== bestDiff[0]);
          continue;
        }
        if (bestDiff.length === 2) {
          const sub = nbaTwoWayTiebreaker(bestDiff, games, options);
          ordered.push(...sub.orderedTeams);
          mergeResults(ctx, sub);
        } else {
          const sub = nbaMultiWayTiebreaker(bestDiff, games, true, options);
          ordered.push(...sub.orderedTeams);
          mergeResults(ctx, sub);
        }
        remaining = remaining.filter(t => !bestDiff.includes(t));
        continue;
      }
    }

    // Step 7 (fallback): Coin flip one at a time
    const w = ctx.coinFlipFn(remaining);
    ctx.rankMethods.set(w.teamAbbreviation, RankMethod.CoinFlip);
    addExplanation(ctx, w, 'Multi-way exhausted. Coin flip.');
    ordered.push(w);
    remaining = remaining.filter(t => t !== w);
  }

  if (remaining.length === 1) {
    ordered.push(remaining[0]);
  }

  return { orderedTeams: ordered, rankMethods: ctx.rankMethods, explanations: ctx.explanations };
}

/**
 * NBA Conference ranker.
 * Does NOT separate division winners (unlike MLB).
 * Groups by win%, dispatches tied groups.
 */
export function nbaConferenceRanker(
  teams: NbaTiebreakerTeam[],
  games: GameResultsLookup,
  options?: NbaTiebreakerOptions,
): TiebreakerResult<NbaTiebreakerTeam> {
  const ctx = createContext<NbaTiebreakerTeam>(games, options);
  const ordered: NbaTiebreakerTeam[] = [];

  const pctGroups = groupByDescending(teams, t => t.records.all.pctNaN);
  for (const group of pctGroups) {
    if (group.length === 1) {
      ctx.rankMethods.set(group[0].teamAbbreviation, RankMethod.AllPCT);
      ordered.push(group[0]);
    } else if (group.length === 2) {
      const sub = nbaTwoWayTiebreaker(group, games, options);
      ordered.push(...sub.orderedTeams);
      mergeResults(ctx, sub);
    } else {
      const sub = nbaMultiWayTiebreaker(group, games, false, options);
      ordered.push(...sub.orderedTeams);
      mergeResults(ctx, sub);
    }
  }

  return { orderedTeams: ordered, rankMethods: ctx.rankMethods, explanations: ctx.explanations };
}

/**
 * NBA Play-in seeding: ranks teams 7-10 in a conference.
 */
export function nbaPlayInSeeding(
  teams: NbaTiebreakerTeam[],
  games: GameResultsLookup,
  options?: NbaTiebreakerOptions,
): TiebreakerResult<NbaTiebreakerTeam> {
  return nbaConferenceRanker(teams, games, options);
}

// ─── Internal helpers ───────────────────────────────────────────────────────

function mkResult(
  ctx: StepContext<NbaTiebreakerTeam>,
  orderedTeams: NbaTiebreakerTeam[],
): TiebreakerResult<NbaTiebreakerTeam> {
  return { orderedTeams, rankMethods: ctx.rankMethods, explanations: ctx.explanations };
}

function mergeResults(
  ctx: StepContext<NbaTiebreakerTeam>,
  sub: TiebreakerResult<NbaTiebreakerTeam>,
): void {
  for (const [k, v] of sub.rankMethods) ctx.rankMethods.set(k, v);
  for (const [k, v] of sub.explanations) {
    const existing = ctx.explanations.get(k) ?? [];
    ctx.explanations.set(k, [...existing, ...v]);
  }
}
